import React from "react";
import SectionLabel from "./SectionLabel";
import RouteStrip from "./RouteStrip";
import Ticket from "./Ticket";
import { daysUntil } from "../lib/format";
import "./DashboardPreview.css";

const STATUS_LABELS = {
  collecting_docs: "Coletando documentos",
  generating_arguments: "Gerando argumentos",
  drafting_letters: "Montando cartas",
  sent: "Enviado",
  replied: "Respondido",
  escalated: "Escalado",
  resolved: "Resolvido",
  archived: "Arquivado",
};

export default function DashboardPreview({
  greeting = {},
  today_label = "",
  summary = {},
  stats = {},
  cases = [],
}) {
  const openCount = stats.open_count ?? 0;
  const resolvedCount = stats.resolved_count ?? 0;
  const activeDeadlines = stats.active_deadlines_count ?? 0;

  return (
    <main className="trunfo-dashboard">
      <header className="trunfo-dashboard__header">
        <div className="trunfo-dashboard__eyebrow">
          <span>Painel</span>
          <span aria-hidden="true">·</span>
          <span>{today_label}</span>
        </div>

        <div className="trunfo-dashboard__headline">
          <h1 className="trunfo-dashboard__title">
            <Greeting greeting={greeting} />{" "}
            <span className="trunfo-dashboard__title-muted">
              <CountHeadline count={openCount} />
            </span>
          </h1>

          <a href="/cases/new" className="trunfo-dashboard__cta">
            <span aria-hidden="true" className="trunfo-dashboard__cta-plus">
              +
            </span>
            Abrir novo caso
          </a>
        </div>

        <p className="trunfo-dashboard__lede">
          <Lede summary={summary} />
        </p>
      </header>

      <section className="trunfo-dashboard__stats" aria-label="Resumo de casos">
        <StatCard label="Em aberto" value={String(openCount)} />
        <StatCard label="Resolvidos" value={String(resolvedCount)} />
        <StatCard label="Prazos ativos" value={String(activeDeadlines)} tone="warn" />
      </section>

      {cases.length === 0 ? (
        <EmptyState />
      ) : (
        <section className="trunfo-dashboard__cases" aria-label="Seus casos">
          {cases.map((c) => (
            <CaseTicket key={c.public_code} caseRecord={c} />
          ))}
        </section>
      )}

      <LegalDisclaimer />
    </main>
  );
}

function LegalDisclaimer() {
  return (
    <aside className="trunfo-dashboard__disclaimer" role="note" aria-label="Aviso legal">
      <span className="trunfo-dashboard__disclaimer-title">Aviso legal</span>
      <p className="trunfo-dashboard__disclaimer-body">
        Trunfo não é um escritório de advocacia e não presta consultoria jurídica. Ajudamos você a
        entender seus direitos e organizar suas informações. Para representação ou aconselhamento
        jurídico, procure um advogado de sua confiança.
      </p>
    </aside>
  );
}

function Greeting({ greeting }) {
  const period = greeting.period ?? "Olá";
  if (!greeting.name) return <>{period}.</>;
  return (
    <>
      {period}, {greeting.name}.
    </>
  );
}

function CountHeadline({ count }) {
  if (count === 0) return <>Nenhum caso em andamento.</>;
  if (count === 1) return <>1 caso em andamento.</>;
  return <>{count} casos em andamento.</>;
}

function Lede({ summary }) {
  const nextDeadline = summary.next_deadline_days;

  if (nextDeadline == null) {
    return <>Nada em andamento agora. Quando abrir um caso, os detalhes aparecem aqui.</>;
  }

  return (
    <>
      Um prazo da ANAC vence em{" "}
      <strong>{nextDeadline === 0 ? "hoje" : `${nextDeadline} dias`}</strong>.
    </>
  );
}

function StatCard({ label, value, tone }) {
  const className = "trunfo-stat" + (tone ? ` trunfo-stat--${tone}` : "");
  return (
    <div className={className}>
      <span className="trunfo-stat__label">{label}</span>
      <span className="trunfo-stat__value">{value}</span>
    </div>
  );
}

function EmptyState() {
  return (
    <div className="trunfo-dashboard__empty">
      <SectionLabel tone="mute">Nenhum caso aberto</SectionLabel>
      <p>Quando um caso começar, ele aparece aqui como um cartão de embarque.</p>
    </div>
  );
}

function CaseTicket({ caseRecord }) {
  const statusLabel = STATUS_LABELS[caseRecord.status] ?? caseRecord.status;
  const deadlineDays = daysUntil(caseRecord.airline_deadline_at);

  return (
    <a href={`/cases/${caseRecord.public_code}`} className="trunfo-dashboard__case-link">
      <Ticket
        header={
          <>
            <SectionLabel>Caso {caseRecord.public_code}</SectionLabel>
            <SectionLabel tone="accent">{statusLabel}</SectionLabel>
          </>
        }
        stub={
          deadlineDays !== null ? (
            <>
              <SectionLabel tone="mute">Prazo cia</SectionLabel>
              <SectionLabel tone="accent">
                {deadlineDays === 0 ? "hoje" : `${deadlineDays} dias`}
              </SectionLabel>
            </>
          ) : null
        }
      >
        <RouteStrip
          origin={caseRecord.origin_iata ?? "—"}
          destination={caseRecord.destination_iata ?? "—"}
          flightCode={caseRecord.flight_code ?? caseRecord.airline_iata ?? ""}
          meta={caseRecord.problem_type ?? caseRecord.title ?? ""}
        />
      </Ticket>
    </a>
  );
}
